import type { LogEvent } from "~/hooks/useLogEvent";
import type { ProxyReason } from "./vercel-proxy";
import type { FetchFailureReason } from "./fetch-failure";

// Marker header set by `vercel.json` `routes.transforms` so the client (and
// browser DevTools Network tab) can verify the response was served via the
// config-based proxy and not by an unexpected fallback. See README/CLAUDE.md.
export const PROXY_VIA_HEADER = "x-ib-proxy-via";

/**
 * How the catch-side proxy retry ended. `failed` means the customer reached
 * neither the API host nor our own origin, so the request is lost.
 */
export const PROXY_RETRY_RESULT = {
    RECOVERED: "recovered",
    FAILED: "failed",
} as const;

export type ProxyRetryResult =
    (typeof PROXY_RETRY_RESULT)[keyof typeof PROXY_RETRY_RESULT];

export const PROXY_RETRY_LOG_EVENT = {
    TAG: "api-network-fallback-result",
    RECOVERED: "Direct fetch threw — vercel.json proxy retry recovered the request",
    FAILED: "Direct fetch threw — vercel.json proxy retry failed too",
} as const;

// Likely shapes of a broken upstream proxy. We see these when:
//   - `BACKEND_API_ORIGIN` env var is not set in Vercel → `dest` becomes
//     literally "$BACKEND_API_ORIGIN/..." → DNS/connection failure
//   - Wrong origin configured → upstream returns 4xx/5xx without our marker
// Logged loudly so misconfiguration is caught in the first deploy, not in
// the first paying user's broken cart.
function looksLikeBrokenProxy(response: Response): boolean {
    const via = response.headers.get(PROXY_VIA_HEADER);
    if (via) return false;
    return response.status >= 400;
}

// Records that a proxied request happened.
//
// In production: routes through `useLogEvent` → Datadog (filter by
// `tag:api-proxy-via` or `tag:api-proxy-misconfigured`).
//
// In non-production (QA preview, local): `useLogEvent` is a no-op by design
// (gated on AppConfig.isProduction). Mirror to the browser console so
// engineers can still see what the proxy did during preview verification.
//
// `via === "vercel-routes"` → config-based proxy served the response.
// `via === null` → marker header missing (config missing/broken/wrong route).
//
// `logEvent` is passed in because this helper lives outside the React hook
// where `useLogEvent()` is called.
export function logProxyVia({
    response,
    proxyUrl,
    reason,
    userId,
    logEvent,
}: {
    response: Response;
    proxyUrl: string;
    reason: ProxyReason;
    userId: string | null | undefined;
    logEvent: LogEvent;
}): void {
    const via = response.headers.get(PROXY_VIA_HEADER);
    const broken = looksLikeBrokenProxy(response);
    const message = broken
        ? "Proxy returned an error without our marker — BACKEND_API_ORIGIN likely missing or misconfigured in Vercel env vars"
        : via
            ? `Proxied response served via ${via}`
            : "Proxied request returned without proxy marker header — verify vercel.json routes";
    const data = {
        via,
        status: response.status,
        proxy_url: proxyUrl,
        reason,
        href: window.location.href,
        user_id: userId,
    };

    logEvent({
        message,
        tag: broken ? "api-proxy-misconfigured" : "api-proxy-via",
        data,
    });

    // Console-visible fallback for QA preview where Datadog SDK is disabled.
    if (process.env.NEXT_PUBLIC_ENV !== "production") {
        if (broken) {
            // eslint-disable-next-line no-console
            console.error(`[ib-proxy] ${message}`, data);
        } else if (via) {
            // eslint-disable-next-line no-console
            console.info(`[ib-proxy] ${message}`, data);
        } else {
            // eslint-disable-next-line no-console
            console.warn(`[ib-proxy] ${message}`, data);
        }
    }
}

/**
 * Records how a catch-side proxy retry ended, one tag for both results, so
 * Datadog can count the customers who cannot reach the API host directly and
 * how many of them the proxy saves. Interstitial retries are not reported
 * here: there the API host answered.
 */
export function logProxyRetryResult({
    result,
    proxyUrl,
    originalUrl,
    method,
    userId,
    logEvent,
    status,
    proxyError,
    proxyFailureReason,
}: {
    result: ProxyRetryResult;
    proxyUrl: string;
    originalUrl: string | undefined;
    method: string;
    userId: string | null | undefined;
    logEvent: LogEvent;
    status?: number;
    proxyError?: string;
    proxyFailureReason?: FetchFailureReason;
}): void {
    const data = {
        result,
        status,
        proxy_url: proxyUrl,
        original_url: originalUrl,
        method,
        proxy_error: proxyError,
        proxy_failure_reason: proxyFailureReason,
        href: window.location.href,
        user_id: userId,
    };

    // Left at the default `info`, like the `api-error` this precedes: most
    // lost requests are fire-and-forget analytics, not a customer action.
    logEvent({
        message:
            result === PROXY_RETRY_RESULT.RECOVERED
                ? PROXY_RETRY_LOG_EVENT.RECOVERED
                : PROXY_RETRY_LOG_EVENT.FAILED,
        tag: PROXY_RETRY_LOG_EVENT.TAG,
        data,
    });
}
